;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import Lockup from 'components/blocks/shared/Lockup';
/* Cards */
import CardAnimatedIcon from 'components/cards/CardAnimatedIcon';
import CardCampaign from 'components/cards/CardCampaign';
import CardDownload from 'components/cards/CardDownload';
import CardDualAspectNews from 'components/cards/CardDualAspectNews';
import CardIconStat from 'components/cards/CardIconStat';
import CardLatest from 'components/cards/CardLatest';
import CardLeadership from 'components/cards/CardLeadership';
import CardNewsDetail from 'components/cards/CardNewsDetail';
import CardPolaroid from 'components/cards/CardPolaroid';
import CardProduct from 'components/cards/CardProduct';
import CardRecipe from 'components/cards/CardRecipe';
import CardSmall from 'components/cards/CardSmall';
import ButtonCTA from 'components/ui/ButtonCTA';

import { blockRootProps, getColor } from 'utils';

import CardGroupCarousel from './CardGroupCarousel';
import InlineIntro from './InlineIntro';

import styles from './BlockCardGroup.module.scss';

const themeConfig = {
    'Full Width': {
        className: 'fullWidth',
        buttonTheme: 'outlined-blue-blue',
    },
    Featured: {
        className: 'featured',
        buttonTheme: 'outlined-cream-cream',
    },
    Default: {
        className: 'default',
        buttonTheme: 'outlined-blue-blue',
    },
    Leadership: {
        className: 'leadership',
        buttonTheme: 'outlined-blue-blue',
    },
    Latest: {
        className: 'latest',
        buttonTheme: 'outlined-blue-blue',
    },
    Events: {
        className: 'events',
        buttonTheme: 'outlined-blue-blue',
    },
    Campaign: {
        className: 'campaign',
        buttonTheme: 'outlined-blue-blue',
    },
    Recipes: {
        className: 'recipes',
        buttonTheme: 'outlined-blue-blue',
    },
    Products: {
        className: 'products',
        buttonTheme: 'outlined-blue-blue',
    },
    Recommended: {
        className: 'recommended',
        buttonTheme: 'outlined-blue-blue',
    },
    Download: {
        className: 'download',
        buttonTheme: 'outlined-blue-blue',
    },
    Ultrabold: {
        className: 'ultrabold',
        buttonTheme: 'outlined-blue-blue',
    },
    'Inline Intro': {
        className: 'inlineIntro',
        buttonTheme: 'filled-blue-ocean',
    },
    'Recipe Products': {
        className: 'recipeProducts',
        buttonTheme: 'outlined-blue-blue',
    },
    Holiday: {
        className: 'holiday',
    },
};

const lockupCopyOverrides = {
    Medium: {
        baseTheme: 'bodyMedium',
        themes: { large: 'bodyLarge' },
    },
    Large: {
        baseTheme: 'bodyMedium',
        themes: { large: 'bodyLarge' },
    },
};

const BlockCardGroup = ({
    __typename,
    anchorId,
    theme,
    inlineIntroIcon,
    eyebrow,
    headline,
    copy,
    cards,
    cta,
    flushTop,
    flushBottom,
    navigationTheme,
    backgroundColor,
    inlineIntroTextColor,
    inlineIntroLockupTheme,
}) => {
    theme ??= 'Default';
    inlineIntroTextColor ??= 'blue';
    backgroundColor ??= 'cream';

    const cardMap = {
        CardAnimatedIcon,
        Recipe: CardRecipe,
        CardCampaign,
        Product: theme === 'Recipe Products' ? CardPolaroid : CardProduct,
        CardLeadership,
        CardDownload,
        CardDualAspectNews,
        CardLatest,
        News: CardNewsDetail,
        CardIconStat,
        CardSmall,
        CardPolaroid,
        InlineIntro,
        Default: null,
    };

    const inlineIntroPlaceholderCard = {
        __typename: 'InlineIntro',
    };

    const renderInlineIntroLockup = () => {
        return (
            <Lockup
                icon={inlineIntroIcon}
                eyebrow={{ text: eyebrow }}
                headline={{
                    text: headline,
                }}
                copy={{
                    text: copy,
                    themeOverrides: lockupCopyOverrides[inlineIntroLockupTheme],
                }}
                ctas={cta ? [cta] : undefined}
                theme={inlineIntroLockupTheme}
            />
        );
    };

    if (!cards) {
        return null;
    }

    return (
        <section
            {...blockRootProps(anchorId, __typename)}
            className={cx(
                styles.cardGroup,
                styles[themeConfig[theme]?.className],
                {
                    [styles.flushTop]: flushTop,
                    [styles.flushBottom]: flushBottom,
                }
            )}
            style={{
                '--background-color':
                    backgroundColor && getColor(backgroundColor),
                '--slider-gap-color': backgroundColor
                    ? getColor(backgroundColor)
                    : getColor('cream'),
                '--inline-intro-color':
                    inlineIntroTextColor && getColor(inlineIntroTextColor),
            }}
        >
            {theme === 'Inline Intro' && headline && (
                <div className={styles.inlineIntroSmall}>
                    {renderInlineIntroLockup()}
                </div>
            )}
            <div className={styles.cardGroupCarousel}>
                {theme === 'Inline Intro' && (
                    <div className={styles.inlineIntroContent}>
                        <div className={styles.inlineIntroContentInner}>
                            {renderInlineIntroLockup()}
                        </div>
                    </div>
                )}
                <CardGroupCarousel
                    className={styles.carousel}
                    cardMap={cardMap}
                    cards={
                        theme === 'Inline Intro'
                            ? [inlineIntroPlaceholderCard, ...cards]
                            : cards
                    }
                    theme={theme}
                    navigationTheme={navigationTheme ?? 'blue'}
                    backgroundColor={backgroundColor}
                />
            </div>
            {theme !== 'Inline Intro' && cta?.link && cta?.text && (
                <ButtonCTA
                    className={styles.cta}
                    style={themeConfig[theme]?.buttonTheme}
                    text={cta.text}
                    {...cta}
                />
            )}
        </section>
    );
};

BlockCardGroup.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    theme: PropTypes.oneOf([
        'Default',
        'Leadership',
        'Latest',
        'Campaign',
        'Inline Intro',
        'Recipe Product',
    ]),
    inlineIntroIcon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            icon: PropTypes.shape({
                url: PropTypes.string,
            }),
        }),
    ]),
    eyebrow: PropTypes.string,
    headline: PropTypes.string,
    cards: PropTypes.array.isRequired,
    copy: PropTypes.object,
    cta: PropTypes.object,
    flushBottom: PropTypes.bool,
    flushTop: PropTypes.bool,
    hasArrowIcon: PropTypes.bool,
    navigationTheme: PropTypes.oneOf(['blue', 'cream', 'orange']),
    backgroundColor: PropTypes.string,
    inlineIntroTextColor: PropTypes.string,
    inlineIntroLockupTheme: PropTypes.oneOf(['Medium', 'Large']),
};

export default BlockCardGroup;
